import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const YEARS_PER_PAGE = 12

const isMonthOutOfRange = (year, monthIndex, minDate, maxDate) => {
  const start = dayjs().year(year).month(monthIndex).startOf('month')
  const end = start.endOf('month')
  if (minDate?.isValid() && end.isBefore(minDate, 'day')) return true
  if (maxDate?.isValid() && start.isAfter(maxDate, 'day')) return true
  return false
}

/** Month is only blocked if neither this year nor year+1 fits maxDate. */
const isMonthSelectionBlocked = (year, monthIndex, minDate, maxDate) => {
  let candidateYear = year
  const thisMonthEnd = dayjs().year(year).month(monthIndex).endOf('month')
  if (minDate?.isValid() && thisMonthEnd.isBefore(minDate, 'day')) {
    candidateYear = year + 1
  }
  const start = dayjs().year(candidateYear).month(monthIndex).startOf('month')
  if (maxDate?.isValid() && start.isAfter(maxDate, 'day')) return true
  return false
}

const isYearOutOfRange = (year, minDate, maxDate) => {
  const start = dayjs().year(year).startOf('year')
  const end = start.endOf('year')
  if (minDate?.isValid() && end.isBefore(minDate, 'day')) return true
  if (maxDate?.isValid() && start.isAfter(maxDate, 'day')) return true
  return false
}

/**
 * Calendar header: clickable month + year open an in-panel picker overlay.
 * Shared by AppDateInput and TransferDateTimePicker (all product search calendars).
 */
const CalendarMonthYearNav = ({
  viewMonth,
  locale = 'fr',
  minDate = null,
  maxDate = null,
  onChange,
  iconBtnClass = '',
  labelClass = 'text-xs font-semibold capitalize text-[var(--foreground)]',
  canGoPrevious = true,
  canGoNext = true,
  onPrevious,
  onNext,
}) => {
  const { t } = useTranslation()
  const [mode, setMode] = useState(null)
  const [yearPageStart, setYearPageStart] = useState(() => {
    const y = viewMonth.year()
    return y - (y % YEARS_PER_PAGE)
  })

  useEffect(() => {
    if (mode !== 'year') return
    const y = viewMonth.year()
    setYearPageStart(y - (y % YEARS_PER_PAGE))
  }, [mode, viewMonth])

  const monthNames = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) =>
        dayjs().locale(locale).month(i).format('MMM')
      ),
    [locale]
  )

  const years = useMemo(
    () => Array.from({ length: YEARS_PER_PAGE }, (_, i) => yearPageStart + i),
    [yearPageStart]
  )

  const handleSelectMonth = (monthIndex) => {
    if (isMonthSelectionBlocked(viewMonth.year(), monthIndex, minDate, maxDate)) return

    let next = viewMonth.month(monthIndex).startOf('month')
    // Past month for the current year → jump to the same month next year
    if (minDate?.isValid() && next.endOf('month').isBefore(minDate, 'day')) {
      next = next.add(1, 'year')
    }
    if (maxDate?.isValid() && next.startOf('month').isAfter(maxDate, 'day')) return

    onChange?.(next)
    setMode(null)
  }

  const handleSelectYear = (year) => {
    if (isYearOutOfRange(year, minDate, maxDate)) return
    let next = viewMonth.year(year).startOf('month')
    if (isMonthOutOfRange(year, next.month(), minDate, maxDate)) {
      const firstValid = Array.from({ length: 12 }, (_, i) => i).find(
        (m) => !isMonthOutOfRange(year, m, minDate, maxDate)
      )
      if (firstValid == null) return
      next = next.month(firstValid)
    }
    onChange?.(next)
    setMode(null)
  }

  const cellClass = (active, disabled) =>
    [
      'flex h-8 w-full items-center justify-center rounded-[var(--radius-md)] text-xs font-medium transition',
      disabled
        ? 'cursor-not-allowed opacity-30'
        : active
          ? 'bg-[var(--primary)] text-[var(--primary-foreground)] shadow-sm'
          : 'text-[var(--foreground)] hover:bg-[var(--muted)]',
    ].join(' ')

  return (
    <div className="relative">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={onPrevious}
          disabled={!canGoPrevious || mode != null}
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-md)] transition disabled:cursor-not-allowed disabled:opacity-40 ${iconBtnClass}`}
          aria-label={t('datePicker.prevMonth', { defaultValue: 'Previous month' })}
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </button>

        <div className="flex min-w-0 flex-1 items-center justify-center gap-1">
          <button
            type="button"
            onClick={() => setMode((m) => (m === 'month' ? null : 'month'))}
            className={`${labelClass} rounded-[var(--radius-sm)] px-1.5 py-0.5 capitalize transition hover:bg-[var(--muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]`}
            aria-haspopup="dialog"
            aria-expanded={mode === 'month'}
            aria-label={t('datePicker.selectMonth', { defaultValue: 'Select month' })}
          >
            {viewMonth.locale(locale).format('MMMM')}
          </button>
          <button
            type="button"
            onClick={() => setMode((m) => (m === 'year' ? null : 'year'))}
            className={`${labelClass} rounded-[var(--radius-sm)] px-1.5 py-0.5 transition hover:bg-[var(--muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]`}
            aria-haspopup="dialog"
            aria-expanded={mode === 'year'}
            aria-label={t('datePicker.selectYear', { defaultValue: 'Select year' })}
          >
            {viewMonth.locale(locale).format('YYYY')}
          </button>
        </div>

        <button
          type="button"
          onClick={onNext}
          disabled={!canGoNext || mode != null}
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-md)] transition disabled:cursor-not-allowed disabled:opacity-40 ${iconBtnClass}`}
          aria-label={t('datePicker.nextMonth', { defaultValue: 'Next month' })}
        >
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>

      {mode ? (
        <div
          className="absolute inset-x-0 top-0 z-20 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--card)] p-3 shadow-[var(--shadow-md)]"
          role="dialog"
          aria-label={
            mode === 'month'
              ? t('datePicker.selectMonth', { defaultValue: 'Select month' })
              : t('datePicker.selectYear', { defaultValue: 'Select year' })
          }
        >
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-[10px] font-bold uppercase tracking-wider text-[var(--primary)]">
              {mode === 'month'
                ? t('datePicker.selectMonth', { defaultValue: 'Select month' })
                : t('datePicker.selectYear', { defaultValue: 'Select year' })}
            </p>
            {mode === 'year' ? (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setYearPageStart((y) => y - YEARS_PER_PAGE)}
                  className={`flex h-7 w-7 items-center justify-center rounded-[var(--radius-md)] transition ${iconBtnClass}`}
                  aria-label={t('datePicker.prevYears', { defaultValue: 'Previous years' })}
                >
                  <ChevronLeftIcon className="h-3.5 w-3.5" />
                </button>
                <span className="min-w-[4.5rem] text-center text-xs font-semibold tabular-nums text-[var(--foreground)]">
                  {yearPageStart}–{yearPageStart + YEARS_PER_PAGE - 1}
                </span>
                <button
                  type="button"
                  onClick={() => setYearPageStart((y) => y + YEARS_PER_PAGE)}
                  className={`flex h-7 w-7 items-center justify-center rounded-[var(--radius-md)] transition ${iconBtnClass}`}
                  aria-label={t('datePicker.nextYears', { defaultValue: 'Next years' })}
                >
                  <ChevronRightIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setMode(null)}
                className="text-[length:var(--font-size-xs)] font-medium text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
              >
                {t('common.close', { defaultValue: 'Close' })}
              </button>
            )}
          </div>

          {mode === 'month' ? (
            <div className="grid grid-cols-3 gap-1.5">
              {monthNames.map((name, monthIndex) => {
                const disabled = isMonthSelectionBlocked(
                  viewMonth.year(),
                  monthIndex,
                  minDate,
                  maxDate
                )
                const active = viewMonth.month() === monthIndex
                return (
                  <button
                    key={name}
                    type="button"
                    disabled={disabled}
                    onClick={() => handleSelectMonth(monthIndex)}
                    className={`${cellClass(active, disabled)} capitalize`}
                    aria-pressed={active}
                  >
                    {name}
                  </button>
                )
              })}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-1.5">
              {years.map((year) => {
                const disabled = isYearOutOfRange(year, minDate, maxDate)
                const active = viewMonth.year() === year
                return (
                  <button
                    key={year}
                    type="button"
                    disabled={disabled}
                    onClick={() => handleSelectYear(year)}
                    className={cellClass(active, disabled)}
                    aria-pressed={active}
                  >
                    {year}
                  </button>
                )
              })}
            </div>
          )}

          {mode === 'year' ? (
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={() => setMode(null)}
                className="text-[length:var(--font-size-xs)] font-medium text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
              >
                {t('common.close', { defaultValue: 'Close' })}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default CalendarMonthYearNav
